import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, RotateCcw, Save } from "lucide-react";

/**
 * Per-supplier pricing config editor (admin only).
 * Lets admin edit markup tiers, tax rate, shipping fee, and min price for each supplier.
 */
export default function SupplierSettings() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingName, setSavingName] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/supplier-settings");
      setSuppliers(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load supplier settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateField = (idx, field, value) => {
    setSuppliers((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const updateTier = (idx, tIdx, field, value) => {
    setSuppliers((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const tiers = [...(s.markup_tiers || [])];
      tiers[tIdx] = { ...tiers[tIdx], [field]: value };
      return { ...s, markup_tiers: tiers };
    }));
  };

  const addTier = (idx) => {
    setSuppliers((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      return { ...s, markup_tiers: [...(s.markup_tiers || []), { max_cost: 0, markup_percent: 0 }] };
    }));
  };

  const removeTier = (idx, tIdx) => {
    setSuppliers((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const tiers = (s.markup_tiers || []).filter((_, j) => j !== tIdx);
      return { ...s, markup_tiers: tiers };
    }));
  };

  const save = async (supplier) => {
    setSavingName(supplier.name);
    try {
      // Sanitize
      const payload = {
        ...supplier,
        tax_rate: Number(supplier.tax_rate) || 0,
        shipping_fee: Number(supplier.shipping_fee) || 0,
        minimum_price: Number(supplier.minimum_price) || 0,
        markup_tiers: (supplier.markup_tiers || []).map((t) => ({
          max_cost: t.max_cost === "" || t.max_cost === null || t.max_cost === undefined ? null : Number(t.max_cost),
          markup_percent: Number(t.markup_percent) || 0,
        })),
      };
      await axios.put(`/admin/supplier-settings/${encodeURIComponent(supplier.name)}`, payload);
      toast.success(`${supplier.name} saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingName(null);
    }
  };

  const resetAll = async () => {
    if (!confirm("Reset ALL supplier settings to factory defaults? This cannot be undone.")) return;
    try {
      const res = await axios.post("/admin/supplier-settings/reset");
      setSuppliers(res.data || []);
      toast.success("Supplier settings reset to defaults");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8" data-testid="supplier-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Supplier Pricing</h2>
          <p className="text-xs text-slate-500">Edit markup, tax, shipping & minimum price per supplier.</p>
        </div>
        <Button variant="outline" size="sm" onClick={resetAll} data-testid="reset-suppliers-btn">
          <RotateCcw className="w-4 h-4 mr-1" /> Reset Defaults
        </Button>
      </div>

      {suppliers.map((s, idx) => (
        <Card key={s.name} className="p-4" data-testid={`supplier-card-${s.name}`}>
          <CardHeader className="p-0 mb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span>{s.name}</span>
              <label className="flex items-center gap-2 text-xs font-normal text-slate-500">
                <Switch
                  checked={!!s.enabled}
                  onCheckedChange={(v) => updateField(idx, "enabled", v)}
                  data-testid={`enabled-${s.name}`}
                />
                {s.enabled ? "Enabled" : "Disabled"}
              </label>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 space-y-4">
            {/* Toggles row */}
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2 text-sm border rounded-md px-3 py-2">
                <Switch
                  checked={!!s.apply_markup}
                  onCheckedChange={(v) => updateField(idx, "apply_markup", v)}
                  data-testid={`apply-markup-${s.name}`}
                />
                Markup
              </label>
              <label className="flex items-center gap-2 text-sm border rounded-md px-3 py-2">
                <Switch
                  checked={!!s.apply_tax}
                  onCheckedChange={(v) => updateField(idx, "apply_tax", v)}
                  data-testid={`apply-tax-${s.name}`}
                />
                Tax
              </label>
              <label className="flex items-center gap-2 text-sm border rounded-md px-3 py-2">
                <Switch
                  checked={!!s.apply_shipping}
                  onCheckedChange={(v) => updateField(idx, "apply_shipping", v)}
                  data-testid={`apply-shipping-${s.name}`}
                />
                Shipping
              </label>
            </div>

            {/* Numbers row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <Label className="text-xs">Tax %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={s.tax_rate ?? 0}
                  onChange={(e) => updateField(idx, "tax_rate", e.target.value)}
                  disabled={!s.apply_tax}
                  data-testid={`tax-rate-${s.name}`}
                />
              </div>
              <div>
                <Label className="text-xs">Shipping $</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={s.shipping_fee ?? 0}
                  onChange={(e) => updateField(idx, "shipping_fee", e.target.value)}
                  disabled={!s.apply_shipping}
                  data-testid={`shipping-fee-${s.name}`}
                />
              </div>
              <div>
                <Label className="text-xs">Minimum $</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={s.minimum_price ?? 0}
                  onChange={(e) => updateField(idx, "minimum_price", e.target.value)}
                  data-testid={`minimum-price-${s.name}`}
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Switch
                    checked={!!s.tax_includes_shipping}
                    onCheckedChange={(v) => updateField(idx, "tax_includes_shipping", v)}
                    disabled={!s.apply_tax || !s.apply_shipping}
                    data-testid={`tax-incl-ship-${s.name}`}
                  />
                  Tax incl. shipping
                </label>
              </div>
            </div>

            {/* Markup tiers */}
            {s.apply_markup && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Markup Tiers (by part cost)</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => addTier(idx)}
                    data-testid={`add-tier-${s.name}`}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Tier
                  </Button>
                </div>
                <div className="space-y-2">
                  {(s.markup_tiers || []).length === 0 && (
                    <p className="text-xs text-slate-400">No tiers — markup will be 0%. Add one or more tiers.</p>
                  )}
                  {(s.markup_tiers || []).map((tier, tIdx) => (
                    <div key={tIdx} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-5">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Max cost ($) — blank = and above"
                          value={tier.max_cost ?? ""}
                          onChange={(e) => updateTier(idx, tIdx, "max_cost", e.target.value)}
                          data-testid={`tier-max-${s.name}-${tIdx}`}
                        />
                      </div>
                      <div className="col-span-1 text-center text-xs text-slate-400">→</div>
                      <div className="col-span-5">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Markup %"
                          value={tier.markup_percent ?? 0}
                          onChange={(e) => updateTier(idx, tIdx, "markup_percent", e.target.value)}
                          data-testid={`tier-pct-${s.name}-${tIdx}`}
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeTier(idx, tIdx)}
                          data-testid={`tier-del-${s.name}-${tIdx}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400">
                  Tip: leave the max-cost field blank for the highest tier (applies to anything above the previous tier).
                </p>
              </div>
            )}

            <div className="pt-2 flex justify-end">
              <Button
                size="sm"
                onClick={() => save(s)}
                disabled={savingName === s.name}
                data-testid={`save-${s.name}`}
              >
                {savingName === s.name ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                Save {s.name}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
